import { useState } from 'react'
import { Keyboard, Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Info, Search } from 'lucide-react-native'
import { colors, isValidMemberSearchQuery } from '@chillout/core'
import { Button, TextField } from '@chillout/ui'
import { Card } from '../../shared/Card'
import { reportUnexpectedError } from '../../../lib/reportError'
import { MemberNotFoundError, useMemberSearch, type CashierMemberProfile } from './useMemberSearch'

interface ManualLookupPanelProps {
  onScanInstead: () => void
  onMemberFound: (member: CashierMemberProfile) => void
}

export function ManualLookupPanel({ onScanInstead, onMemberFound }: ManualLookupPanelProps) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [error, setError] = useState<string | null>(null)
  const memberSearch = useMemberSearch()

  const handleSearch = () => {
    if (!isValidMemberSearchQuery(query)) {
      setError(t('cashierHome.searchFormatError'))
      return
    }
    setError(null)
    memberSearch.mutate(query.trim(), {
      onSuccess: (member) => {
        setQuery('')
        memberSearch.reset()
        onMemberFound(member)
      },
      onError: (err) => {
        if (err instanceof MemberNotFoundError) {
          setError(t('cashierHome.searchNotFoundError'))
        } else {
          reportUnexpectedError(err)
          setError(t('cashierHome.searchNetworkError'))
        }
      },
    })
  }

  const handleQueryChange = (value: string) => {
    setQuery(value)
    if (error) setError(null)
    if (memberSearch.data) memberSearch.reset()
  }

  const handleScanInstead = () => {
    Keyboard.dismiss()
    onScanInstead()
  }

  return (
    <View className="flex-1 items-center justify-center px-6 pb-10">
      <Card className="w-full max-w-[480px] px-8 py-10">
        <Text className="text-center text-3xl font-bold text-primary">
          {t('cashierHome.manualLookupTitle')}
        </Text>
        <Text className="mt-2 text-center text-base lg:text-lg text-secondary">
          {t('cashierHome.manualLookupSubtitle')}
        </Text>

        <TextField
          label={t('cashierHome.searchAccessibilityLabel')}
          prefix={Search}
          value={query}
          onChangeText={handleQueryChange}
          onSubmitEditing={handleSearch}
          placeholder={t('cashierHome.searchPlaceholder')}
          autoCapitalize="none"
          autoCorrect={false}
          keyboardType="email-address"
          returnKeyType="search"
          error={error ?? undefined}
          className="mt-6"
        />

        <Button
          label={t('cashierHome.searchButton')}
          onPress={handleSearch}
          disabled={query.trim().length === 0}
          loading={memberSearch.isPending}
          className="mt-4"
        />

        <View className="mt-6 flex-row items-start gap-3">
          <Info size={20} color={colors.text.secondary} strokeWidth={1.8} style={{ marginTop: 2 }} />
          <View className="flex-1">
            <Text className="text-base lg:text-lg font-semibold text-primary">
              {t('cashierHome.exactMatchOnly')}
            </Text>
            <Text className="mt-0.5 text-base lg:text-lg text-secondary">
              {t('cashierHome.exactMatchDescription')}
            </Text>
          </View>
        </View>
      </Card>

      <View className="items-center pb-10 pt-6">
        <Button
          label={t('cashierHome.scanInstead')}
          onPress={handleScanInstead}
          variant="ghost"
          fullWidth={false}
          accessibilityLabel={t('cashierHome.scanInsteadAccessibilityLabel')}
        />
      </View>
    </View>
  )
}